import React, { memo, useCallback, useState, MouseEvent } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Button } from 'transfix-ui/components/Button'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { makeStyles } from 'transfix-ui/core'
import { IShipmentOfferState } from 'types/FMSGraphqlTypes'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { usePostWaterfallReoffer } from 'apps/fleet-planner/types/apiTypes'
import { useModalState } from 'utils/hooks'
import { TEST_IDS } from '../../constants'
import { isReOfferEligible } from '../../utils/isReOfferEligible'
import DeclineShipmentOfferConfirmationModal from '../DeclineShipmentOfferConfirmationModal'

const useShipmentOfferRowButtonsStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gap: theme.spacing(1),
    '&.single-button': {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    '&.double-button': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  buttonWrapper: {
    '&.accept': {
      order: 1,
      [theme.breakpoints.up('md')]: {
        order: 2,
      },
    },
    '&.decline': {
      order: 2,
      [theme.breakpoints.up('md')]: {
        order: 1,
      },
    },
  },
}))

export interface IShipmentOfferRowButtonsProps {
  shipmentId: string
  offeredState: IShipmentOfferState
  onDeclineSuccess?: () => void
}

const ShipmentOfferRowButtons = memo<IShipmentOfferRowButtonsProps>(
  ({ offeredState, onDeclineSuccess, shipmentId }) => {
    const { shipmentOfferRowButtons } = TEST_IDS
    const classes = useShipmentOfferRowButtonsStyles()
    const { isMobile } = useBreakpointContext()
    const history = useHistory()
    const location = useLocation()
    const { trackEvent } = useFMSAnalytics()

    const isReOffer = isReOfferEligible({ offeredState })

    const [isReOfferError, setIsReOfferError] = useState<boolean>(false)

    const {
      loading: isPostWaterfallReofferLoading,
      mutate: postWaterfallReoffer,
    } = usePostWaterfallReoffer({ id: Number(shipmentId) })

    const {
      modalState: { declineOfferModal },
      toggleModal,
    } = useModalState({
      declineOfferModal: false,
    })

    const navigateToRateCon = useCallback(() => {
      history.push(`/book/${shipmentId}/rate-confirmation`)
    }, [history, shipmentId])

    const reOfferShipment = useCallback(async () => {
      try {
        setIsReOfferError(false)
        await postWaterfallReoffer()
        trackEvent('Offers', 'SUCCESSFULLY_REOFFERED', { shipmentId })
        navigateToRateCon()
      } catch (e) {
        trackEvent('Offers', 'FAILED_TO_REOFFER', { shipmentId })
        setIsReOfferError(true)
      }
    }, [navigateToRateCon, postWaterfallReoffer, shipmentId, trackEvent])

    const clickAcceptHandler = useCallback(
      (e: MouseEvent<HTMLLabelElement>) => {
        e.stopPropagation()

        if (location.pathname === '/home') {
          trackEvent('Offers', 'CLICKED_ACCEPT_OFFER_BUTTON_HOMEPAGE', {
            shipmentId,
          })
        } else if (location.pathname === '/offers') {
          trackEvent('Offers', 'CLICKED_ACCEPT_OFFER_BUTTON_OFFERS_PAGE', {
            shipmentId,
          })
        }

        if (offeredState === IShipmentOfferState.DedicatedRateOfferedToMoreCarriers) {
          reOfferShipment()
        } else {
          navigateToRateCon()
        }
      },
      [location.pathname, navigateToRateCon, offeredState, reOfferShipment, shipmentId, trackEvent]
    )

    const closeModalHandler = useCallback(() => {
      toggleModal('declineOfferModal', false)
    }, [toggleModal])

    const clickDeclineHandler = useCallback(
      (e: MouseEvent<HTMLLabelElement>) => {
        e.stopPropagation()

        if (location.pathname === '/home') {
          trackEvent('Offers', 'CLICKED_DECLINE_OFFER_BUTTON_HOMEPAGE', {
            shipmentId,
          })
        } else if (location.pathname === '/offers') {
          trackEvent('Offers', 'CLICKED_DECLINE_OFFER_BUTTON_OFFERS_PAGE', {
            shipmentId,
          })
        }

        toggleModal('declineOfferModal', true)
      },
      [location.pathname, shipmentId, toggleModal, trackEvent]
    )

    const declineSuccessHandler = useCallback(() => {
      closeModalHandler()
      if (onDeclineSuccess) {
        onDeclineSuccess()
      }
    }, [closeModalHandler, onDeclineSuccess])

    return (
      <div data-test={shipmentOfferRowButtons.container}>
        {isReOfferError && (
          <InlineMessage
            message={"There's an issue accepting this offer. Contact Support."}
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            dataTest={shipmentOfferRowButtons.errorMessage}
          />
        )}
        <div className={`${classes.container} ${isReOffer ? 'single-button' : 'double-button'}`}>
          {!isReOffer && (
            <div className={`${classes.buttonWrapper} decline`}>
              <Button
                onClick={clickDeclineHandler}
                color='tertiary'
                size='large'
                fullWidth
                data-test={shipmentOfferRowButtons.declineButton}>
                Decline
              </Button>
            </div>
          )}
          <div className={`${classes.buttonWrapper} accept`}>
            <Button
              onClick={clickAcceptHandler}
              color={isMobile ? 'primary' : 'secondary'}
              size='large'
              fullWidth
              disabled={isPostWaterfallReofferLoading || isReOfferError}
              data-test={shipmentOfferRowButtons.acceptButton}>
              Accept Offer
            </Button>
          </div>
        </div>
        {declineOfferModal && (
          <DeclineShipmentOfferConfirmationModal
            onClose={closeModalHandler}
            externalId={shipmentId}
            onDeclineSuccess={declineSuccessHandler}
          />
        )}
      </div>
    )
  }
)

export default ShipmentOfferRowButtons
